import React, { useContext, useState, useRef } from 'react';
import { Form, SecondaryButton, ButtonIcon, PhoneNumberTextField } from '@hz-design-system/web-ui';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { currency } from '@ecg-marktplaats/aurora-js-formatters';

import convertToCents from '../../utils/convertToCents';
import getPhoneNumberErrorKey from '../../../../utils/getPhoneNumberErrorKey';

import { TListingClient } from 'types/TListing';

import Classes from './BiddingForm.scss';
import EnvironmentContext from '../../../../contexts/EnvironmentContext';

import BiddingAmountInput from './BiddingAmountInput';

export type TBiddingFormProps = {
  currentMinimumBid: TListingClient['bidsInfo']['currentMinimumBid'];
  amount: string;
  setAmount: (amount: string) => void;
  setPhoneNumber: Function;
  onSubmit: Function;
  withPhoneNumber: boolean;
  phoneNumber?: string;
};

const BiddingForm = ({
  currentMinimumBid = 0,
  amount,
  setAmount,
  setPhoneNumber,
  onSubmit,
  withPhoneNumber,
  phoneNumber,
}: TBiddingFormProps) => {
  const { t } = useI18nContext();
  const { countryCode } = useContext(EnvironmentContext);
  const [errorAmountMessage, setErrorAmountMessage] = useState('');
  const [errorPhoneNumberMessage, setErrorPhoneNumberMessage] = useState('');

  const amountInputRef = useRef<HTMLInputElement>(null);
  const phoneNumberInputRef = useRef<HTMLInputElement>(null);

  const minimumBidLabel =
    currentMinimumBid > 0 ? t('bidding.label', { minimumBid: currency(currentMinimumBid, true) }) : '';

  const validateAmount = (amountValue: string): string => {
    if (!amountValue) {
      return t('bidding.errors.emptyValue');
    }
    const amountInCents = convertToCents(amountValue);
    if (amountInCents <= 0 || amountInCents < currentMinimumBid) {
      return t('bidding.errors.tooLow');
    }
    return '';
  };

  const validatePhoneNumber = (phoneNumberValue: string | undefined): string => {
    if (phoneNumberValue) {
      const phoneNumberErrorKey = getPhoneNumberErrorKey(phoneNumberValue, countryCode);
      return phoneNumberErrorKey ? t(phoneNumberErrorKey) : '';
    }
    return '';
  };

  const handleAmountChange = (bid: string) => {
    setAmount(bid);
    setErrorAmountMessage('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const amountError = validateAmount(amount);
    const phoneError = validatePhoneNumber(phoneNumber);

    setErrorAmountMessage(amountError);
    setErrorPhoneNumberMessage(phoneError);

    if (amountError) {
      amountInputRef.current?.focus();
      amountInputRef.current?.select();
    } else if (phoneError) {
      phoneNumberInputRef.current?.focus();
      phoneNumberInputRef.current?.select();
    }

    if (!amountError && !phoneError) {
      await onSubmit();
      setErrorAmountMessage('');
      setErrorPhoneNumberMessage('');
    }
  };

  const onPhoneChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setErrorPhoneNumberMessage('');
    setPhoneNumber(e.target.value);
  };

  const onFormInvalid = (e: React.FormEvent<HTMLElement>): void => {
    if (e.target['id'] === 'amount') {
      // prevent default error popup
      e.preventDefault();
      setErrorAmountMessage(t('bidding.errors.wrongFormat'));
    }
  };

  return (
    <Form onSubmit={handleSubmit} onInvalid={onFormInvalid}>
      <BiddingAmountInput
        id="amount"
        labelText={minimumBidLabel}
        ariaLabel={t('bidding.inputLabel')}
        prefixText="€"
        value={amount}
        errorText={errorAmountMessage}
        onAmountChange={handleAmountChange}
        inputRef={amountInputRef}
        isLive={document.activeElement === amountInputRef.current}
      />
      {withPhoneNumber && (
        <PhoneNumberTextField
          labelText={t('bidding.phonenumber')}
          type="tel"
          onChange={onPhoneChange}
          onBlur={() => setErrorPhoneNumberMessage('')}
          value={phoneNumber ?? ''}
          errorText={errorPhoneNumberMessage}
          inputRef={phoneNumberInputRef}
          isLive={document.activeElement === phoneNumberInputRef.current}
          PhoneNumberLabel={t('bidding.phonenumber')}
          defaultCountryCode={countryCode}
        />
      )}

      <SecondaryButton type="submit" className={Classes.button}>
        <ButtonIcon name="placebid" isStartIcon={false} />
        <Trans className={Classes.text} tagName="span" i18nKey="bidding.button" />
      </SecondaryButton>
    </Form>
  );
};

export default BiddingForm;
